import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLine, FadeUp, Overline } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { ProjectTile } from "../components/ProjectTile";
import { IMAGES, SERVICES, PROJECTS } from "../lib/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={IMAGES.heroFluid} alt="" className="w-full h-[125%] object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-paper/70 via-paper/60 to-paper" />
      </motion.div>

      <div className="aurora aurora-float w-[26rem] h-[26rem] -top-24 -left-24 opacity-30 -z-10" style={{ background: "#E0146B" }} aria-hidden="true" />
      <div className="aurora aurora-float w-[22rem] h-[22rem] top-1/4 -right-16 opacity-25 -z-10" style={{ background: "#0096C7", animationDelay: "4s" }} aria-hidden="true" />
      <div className="aurora aurora-float w-[18rem] h-[18rem] bottom-10 left-1/3 opacity-25 -z-10" style={{ background: "#FF8A00", animationDelay: "8s" }} aria-hidden="true" />

      <div className="absolute -right-40 top-24 w-[34rem] h-[34rem] rounded-full border border-magenta/20 orbit-ring -z-10 hidden lg:block" aria-hidden="true">
        <span className="absolute -top-2 left-1/2 w-4 h-4 rounded-full bg-magenta drop-shadow-[0_0_15px_rgba(224,20,107,0.6)]" />
      </div>
      <div className="absolute -left-56 bottom-40 w-[28rem] h-[28rem] rounded-full border border-cyan/15 orbit-ring -z-10 hidden lg:block" style={{ animationDirection: "reverse" }} aria-hidden="true">
        <span className="absolute top-1/2 -right-1.5 w-3 h-3 rounded-full bg-cyan drop-shadow-[0_0_12px_rgba(0,150,199,0.6)]" />
      </div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-6 md:px-12 w-full pt-40 pb-16 md:pb-24">
        <MaskedLine delay={0.15}>
          <Overline data-testid="hero-overline">Inubaan Labs — Software Studio</Overline>
        </MaskedLine>

        <h1 className="mt-8 font-display font-black text-[13vw] md:text-[7.6vw] leading-[0.92] tracking-tighter" data-testid="hero-heading">
          <MaskedLine delay={0.3}>IDEAS TODAY.</MaskedLine>
          <MaskedLine delay={0.45} innerClassName="brand-gradient-text drop-shadow-[0_0_25px_rgba(224,20,107,0.25)]">
            IMPACT
          </MaskedLine>
          <MaskedLine delay={0.6}>TOMORROW.</MaskedLine>
        </h1>

        <div className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <MaskedLine delay={0.8} className="max-w-xl">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed" data-testid="hero-subtext">
              We design and engineer web apps, mobile products, cloud platforms and
              AI systems for teams building a better tomorrow.
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 text-white font-semibold rounded-full pl-7 pr-6 py-4 bg-[linear-gradient(100deg,#FF8A00,#E0146B,#0096C7)] bg-[length:200%_100%] bg-left transition-[background-position] duration-500 hover:bg-right"
            >
              Start a project
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-ink/20 text-ink font-semibold rounded-full px-7 py-4 transition-colors duration-300 hover:border-cyan hover:text-cyan"
            >
              Explore services
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-8 hidden md:flex items-center gap-3 text-slate-500"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
};

const ServicesPreview = () => (
  <section data-testid="home-services-section" className="py-32 md:py-44">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeUp>
        <Overline>What we do</Overline>
        <h2 className="mt-6 font-display font-bold text-4xl md:text-6xl tracking-tighter leading-[1.05] max-w-3xl">
          Four crafts. <span className="text-outline-accent">One studio.</span>
        </h2>
      </FadeUp>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
        {SERVICES.slice(0, 4).map((s, i) => (
          <FadeUp
            key={s.title}
            delay={i * 0.08}
            className={i % 4 === 0 || i % 4 === 3 ? "md:col-span-7" : "md:col-span-5"}
          >
            <Link
              to="/services"
              data-testid={`home-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              style={{ "--c": s.color, backgroundColor: `${s.color}0D`, borderColor: `${s.color}30` }}
              className="group flex flex-col h-full border rounded-2xl p-8 md:p-10 transition-[border-color,transform,box-shadow] duration-500 hover:border-[color:var(--c)] hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_var(--c)]"
            >
              <div className="flex items-start justify-between">
                <span
                  className="w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6"
                  style={{ background: `linear-gradient(135deg, ${s.color}, ${s.color}B3)` }}
                >
                  <s.icon className="w-7 h-7 text-white" strokeWidth={1.75} />
                </span>
                <span className="font-mono text-xs font-medium" style={{ color: s.color }}>0{i + 1}</span>
              </div>
              <h3 className="mt-8 font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight break-words">{s.title}</h3>
              <p className="mt-4 text-slate-600 text-base leading-relaxed">{s.description}</p>
              <div className="mt-auto pt-8 flex items-center gap-2 text-sm font-semibold transition-colors duration-300" style={{ color: s.color }}>
                Learn more <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const WorkPreview = () => (
  <section data-testid="home-work-section" className="py-32 md:py-44">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <Overline>Selected work</Overline>
          <h2 className="mt-6 font-display font-bold text-4xl md:text-6xl tracking-tighter leading-[1.05]">
            Built to <span className="brand-gradient-text">ship.</span>
          </h2>
        </div>
        <Link
          to="/work"
          data-testid="home-work-viewall"
          className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors duration-300 hover:text-cyan"
        >
          View all work <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
        </Link>
      </FadeUp>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
        {PROJECTS.slice(0, 2).map((p, i) => (
          <FadeUp key={p.name} delay={i * 0.12} className={i === 0 ? "md:col-span-7" : "md:col-span-5"}>
            <Link to="/work" data-testid={`home-project-${p.name.toLowerCase()}`} className="group block">
              <div className="relative overflow-hidden rounded-2xl border border-ink/10">
                {p.image ? (
                  <>
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-80 md:h-[26rem] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 transition-colors duration-500 group-hover:bg-black/20" />
                  </>
                ) : (
                  <ProjectTile monogram={p.monogram} color={p.color} className="h-80 md:h-[26rem]" />
                )}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: "#22D3EE" }}>{p.category}</p>
                  <h3 className="mt-2 font-display font-bold text-3xl tracking-tight text-white">{p.name}</h3>
                </div>
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ManifestoTeaser = () => (
  <section data-testid="home-manifesto-section" className="py-32 md:py-44 border-t border-ink/10">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
      <FadeUp className="md:col-span-5">
        <Overline>The manifesto</Overline>
        <h2 className="mt-6 font-display font-bold text-4xl md:text-6xl tracking-tighter leading-[1.05]">
          Why we <span className="text-outline-accent">exist.</span>
        </h2>
        <Link
          to="/about"
          data-testid="home-manifesto-link"
          className="group mt-10 inline-flex items-center gap-2 border border-ink/20 rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300 hover:border-magenta hover:text-magenta"
        >
          Read the manifesto <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
        </Link>
      </FadeUp>
      <div className="md:col-span-7 space-y-10">
        {[
          { n: "01", t: "Every idea deserves a prototype, not a pitch deck.", c: "#E0146B" },
          { n: "02", t: "Software is a craft — we sweat the last 2%.", c: "#0096C7" },
          { n: "03", t: "Impact is the only metric that compounds.", c: "#FF8A00" },
        ].map((m, i) => (
          <FadeUp key={m.n} delay={i * 0.1} className="flex gap-8 items-baseline border-b border-ink/10 pb-10">
            <span className="font-mono text-sm" style={{ color: m.c }}>{m.n}</span>
            <p className="font-display font-semibold text-xl md:text-2xl tracking-tight text-slate-700">{m.t}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const CtaBanner = () => (
  <section data-testid="home-cta-section" className="py-32 md:py-48 relative overflow-hidden">
    <div className="absolute inset-0 -z-10">
      <img src={IMAGES.control} alt="" className="w-full h-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-gradient-to-b from-paper via-paper/70 to-paper" />
    </div>
    <div className="aurora aurora-float w-[24rem] h-[24rem] -top-16 right-10 opacity-25 -z-10" style={{ background: "#7C3AED" }} aria-hidden="true" />
    <div className="aurora aurora-float w-[20rem] h-[20rem] bottom-0 -left-16 opacity-25 -z-10" style={{ background: "#0D9488", animationDelay: "5s" }} aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 md:px-12 text-left">
      <FadeUp>
        <Overline>Next chapter</Overline>
        <h2 className="mt-6 font-display font-black text-5xl md:text-8xl tracking-tighter leading-[0.95] max-w-5xl">
          Have an idea?<br />
          <span className="brand-gradient-text">Let's build it.</span>
        </h2>
        <Link
          to="/contact"
          data-testid="home-cta-bottom-button"
          className="group mt-12 inline-flex items-center gap-3 text-white font-semibold rounded-full pl-8 pr-7 py-5 text-lg bg-[linear-gradient(100deg,#FF8A00,#E0146B,#0096C7)] bg-[length:200%_100%] bg-left transition-[background-position] duration-500 hover:bg-right"
        >
          Start a project
          <ArrowUpRight className="w-6 h-6 transition-transform duration-300 group-hover:rotate-45" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <ServicesPreview />
      <WorkPreview />
      <ManifestoTeaser />
      <CtaBanner />
    </>
  );
}
